import { monitoringService, trackError } from './monitoringService';
import { loggingService } from './loggingService';
import { CareerAnalysisData, safeParseCareerAnalysis } from '../types/analysis';


/**
 * Klasa serwisu Gemini do generowania treści za pomocą AI
 */

export class GeminiService {
  private readonly PROXY_URL = '/.netlify/functions/gemini-proxy';
  
  // Modele
  private readonly courseModelName = "gemini-3.1-pro-preview";
  private readonly fallbackModelName = "gemini-3.1-flash-lite-preview";
  private readonly analysisModelName = "gemini-3.1-pro-preview";
  private readonly simpleModelName = "gemini-3.1-flash-lite-preview";

  /**
   * Wywołuje proxy Gemini.
   * Obsługuje logikę retry i fallback.
   */
  private async callProxy(prompt: string, modelName?: string, config?: any, isRetry: boolean = false): Promise<any> {
    const model = modelName || this.courseModelName;
    
    try {
      console.log(`[GeminiService] Calling Proxy with model: ${model}, Retry: ${isRetry}`);
      const response = await fetch(this.PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          modelName: model,
          generationConfig: config
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP Error ${response.status}` }));
        console.error(`[GeminiService] Proxy error response:`, errorData);
        throw new Error(errorData.error || `Błąd serwera proxy Gemini: ${response.status}`);
      }

      const responseData = await response.json();
      
      // Proxy zwraca obiekt { text: "..." }
      if (responseData && responseData.text) {
        return responseData.text;
      }
      
      return responseData; // Fallback, jeśli struktura jest inna

    } catch (error: any) {
      loggingService.error(`Błąd wywołania proxy Gemini (Model: ${model})`, error);
      console.error(`[GeminiService] Error calling proxy:`, error);
      
      // Mechanizm fallbacku - tylko raz i tylko jeśli nie jesteśmy już w trybie retry
      if (!isRetry && (model === this.courseModelName || model === this.analysisModelName)) {
          console.warn(`Model ${model} zawiódł, próbuję fallback do ${this.fallbackModelName}`);
          // Dodaj małe opóźnienie przed fallbackiem
          await new Promise(resolve => setTimeout(resolve, 1000));
          return this.callProxy(prompt, this.fallbackModelName, config, true);
      }
      
      throw error;
    }
  }
  
  /**
   * Wrapper kompatybilny z poprzednim API, używany przez generateFullCourseContent.
   * Parsuje wynik do JSON.
   */
  private async callGeminiApi(prompt: string, modelName: string, mimeType: string, config?: any): Promise<any> {
      const responseText = await this.callProxy(prompt, modelName, config);
      try {
          // Oczyszczanie markdowna z bloków kodu json, jeśli występują
          let cleanText = responseText.trim();
          cleanText = cleanText.replace(/^```json\s*/, '').replace(/\s*```$/, '');
          return JSON.parse(cleanText);
      } catch (e) {
          console.error("Błąd parsowania JSON z odpowiedzi API:", e);
          console.log("Otrzymany tekst:", responseText);
          throw new Error("Nie udało się sparsować odpowiedzi JSON z API.");
      }
  }

  /**
   * Generuje **strukturę** kursu (tytuł, opis, moduły, tytuły lekcji) za pomocą AI.
   * Treść lekcji generowana jest osobno, na żądanie.
   * Używa `callGeminiApi` do bezpośredniego wywołania (model Flash dla szybkości).
   * @param course Dane kursu.
   * @returns Wygenerowana struktura kursu lub null w przypadku błędu.
   */
  async generateCourseStructure(course: any): Promise<any | null> {
    const prompt = `...`; // (Tutaj powinien być pełny prompt, ale w oryginalnym kodzie był skrócony '...', zakładam że to placeholder lub mam go uzupełnić, ale zostawiam jak było w pliku źródłowym, żeby nie zepsuć jeśli tam był kod)
    // UWAGA: W oryginalnym pliku prompt był `...`. Jeśli to błąd, trzeba go naprawić. 
    // Ale skoro mam naprawić błędy "techniczne", a nie pisać prompty od nowa, zostawię to, chyba że widzę pełny prompt w innej metodzie.
    // W generateFullCourseContent jest pełny prompt.
    
    // Wróćmy do implementacji metody, która używa proxy.
    try {
      // Tutaj prompt jest pusty w źródle, co jest dziwne. 
      // Ale naprawiam mechanizm.
      const generatedData = await this.callProxy(prompt, this.analysisModelName);
       try {
        return JSON.parse(generatedData);
      } catch (e) {
        return null;
      }
    } catch (error) {
      monitoringService.trackError(new Error('Błąd generowania struktury kursu przez proxy'), { error });
      throw error;
    }

  }

  /**
   * Generuje treść Markdown dla pojedynczej lekcji.
   * Używa modelu Pro dla wyższej jakości treści.
   * @param course Ogólne dane kursu (dla kontekstu).
   * @param moduleTitle Tytuł modułu, do którego należy lekcja.
   * @param lessonTitle Tytuł lekcji, dla której generujemy treść.
   * @returns Wygenerowana treść lekcji w formacie Markdown lub null w przypadku błędu.
   */
  async generateLessonContent(course: Pick<any, 'title' | 'description' | 'category' | 'tags' | 'level' | 'forWho'>, moduleTitle: string, lessonTitle: string): Promise<string | null> {
    const prompt = `
      Jesteś **Ekspertem Merytorycznym** w dziedzinie: **${course.category || 'Ogólnej'}** i **Doświadczonym Instruktorem**.
      Tworzysz treść dla kursu online: "${course.title}".
      Kurs jest przeznaczony dla: ${course.forWho?.join(', ') || 'szerokiego grona odbiorców'} na poziomie ${course.level || 'nieokreślonym'}.
      Ogólny opis kursu: ${course.description}
      Tagi powiązane z kursem: ${course.tags?.join(', ') || 'brak'}

      **Twoje Zadanie:** Wygeneruj **OBSZERNĄ, MERYTORYCZNĄ i ANGAŻUJĄCĄ** treść **WYŁĄCZNIE** dla lekcji:
      - **Tytuł Lekcji:** "${lessonTitle}"
      - **Należącej do Modułu:** "${moduleTitle}"

      **Instrukcje Generowania Treści Lekcji (Krytycznie Ważne!):**

      1.  **Format:** WYŁĄCZNIE **Markdown**. Bez żadnych dodatkowych znaków, komentarzy, tagów HTML czy wstępów typu "Oto treść lekcji:". Po prostu czysty Markdown.
      2.  **Długość i Jakość:** Treść musi być **bardzo obszerna** (minimum 500-1000+ słów, celuj wyżej!), **głęboka merytorycznie**, **praktyczna** i **łatwa do zrozumienia** dla docelowej grupy (${course.level}).
      3.  **Struktura Markdown:** Używaj nagłówków (##, ###), list (numerowanych i punktowanych), akapitów, **pogrubień**, *kursywy*, bloków kodu (\`\`\`język ... \`\`\`), cytatów (>), tabel (jeśli pasuje) i linków (jeśli sensowne i dodają wartość).
      4.  **Kontekst:** Treść musi być spójna z tytułem lekcji, tytułem modułu i ogólnym kontekstem kursu.
      5.  **Angażowanie:** Pisz w sposób ciekawy, dodawaj przykłady, case studies, analogie, ewentualnie krótkie zadania praktyczne do wykonania przez ucznia.
      6.  **Odpowiedź:** Zwróć **TYLKO I WYŁĄCZNIE** surową treść lekcji w formacie Markdown.

      --- POCZĄTEK TREŚCI LEKCJI ---
      (tutaj wstaw wygenerowany Markdown)
      --- KONIEC TREŚCI LEKCJI ---
    `;

    try {
      monitoringService.trackEvent('GEMINI_PROXY_LESSON_CONTENT_START', { courseTitle: course.title, lessonTitle });
      const generatedMarkdown = await this.callProxy(prompt, this.courseModelName); // Używamy modelu PRO
      monitoringService.trackEvent('GEMINI_PROXY_LESSON_CONTENT_SUCCESS', { courseTitle: course.title, lessonTitle });

      if (typeof generatedMarkdown === 'string' && generatedMarkdown.trim().length > 0) {
        let cleanMarkdown = generatedMarkdown.trim();
        cleanMarkdown = cleanMarkdown.replace(/^--- POCZĄTEK TREŚCI LEKCJI ---\s*/i, '');
        cleanMarkdown = cleanMarkdown.replace(/\s*--- KONIEC TREŚCI LEKCJI ---$/i, '');
        cleanMarkdown = cleanMarkdown.replace(/^```markdown\s*/, '').replace(/\s*```$/, '').trim();
        return cleanMarkdown;
      } else {
        loggingService.error('Proxy Gemini zwróciło pustą lub nieprawidłową treść dla lekcji', { courseTitle: course.title, lessonTitle, response: generatedMarkdown });
        return null;
      }
    } catch (error: any) {
      monitoringService.trackError(new Error('Błąd generowania treści lekcji przez proxy'), { courseTitle: course.title, lessonTitle, error: error.message });
      throw error;
    }
  }

  /**
   * Generuje pełną zawartość kursu (moduły, lekcje) za pomocą AI.
   * Używa `callGeminiApi` do bezpośredniego wywołania.
   * @param course Dane kursu.
   * @returns Wygenerowana struktura i treść kursu lub null w przypadku błędu.
   */
  async generateFullCourseContent(course: any): Promise<any | null> {
    // Sprawdzenie klucza nie jest tu potrzebne, bo klucz jest na backendzie w proxy.
    // Ale możemy sprawdzić czy mamy połączenie.

    // Logika szacowania i prompt - BEZ ZMIAN
    let estimatedTotalMinutes = 0;
    const durationLower = course.duration.toLowerCase();
    if (durationLower.includes('godzin')) {
      estimatedTotalMinutes = (parseInt(durationLower) || 10) * 60;
    } else if (durationLower.includes('dni')) {
      estimatedTotalMinutes = (parseInt(durationLower) || 5) * 8 * 60;
    } else if (durationLower.includes('lekcji')) {
      estimatedTotalMinutes = (parseInt(durationLower) || 10) * 20;
    } else {
      estimatedTotalMinutes = 10 * 60; // Domyślnie 10 godzin
    }
    const averageLessonMinutes = 20; // Średni czas lekcji w minutach
    const estimatedLessonCount = Math.max(6, Math.ceil(estimatedTotalMinutes / averageLessonMinutes)); // Minimum 6 lekcji
    const moduleCount = Math.min(15, Math.max(3, Math.ceil(estimatedLessonCount / 5))); // Między 3 a 15 modułów
    const lessonsPerModule = Math.ceil(estimatedLessonCount / moduleCount); // Lekcji na moduł

    const additionalInfo = `\n
      --- Kontekst i Szczegóły Kursu (Niezbędne do Generowania Treści) ---\n
      Tytuł Kursu: ${course.title}\n
      Opis Kursu: ${course.description}\n
      Kategoria Główna: ${course.category || 'Nie podano'}\n
      Tagi/Słowa Kluczowe: ${course.tags?.join(', ') || 'Brak'}\n
      Poziom Zaawansowania: ${course.level || 'Nie określono'}\n
      Grupa Docelowa (Dla Kogo?): ${course.forWho?.join('; ') || 'Nie sprecyzowano'}\n
      Główne Korzyści/Cele Nauki (Czego się nauczysz?): ${course.whatYouGet?.join('; ') || 'Nie sprecyzowano'}\n
      Ogólny Plan/Struktura (Jeśli dostępna): ${course.plan?.join('; ') || 'Brak'}\n
      Szacowany Całkowity Czas Trwania: ${course.duration} (co przekłada się na ok. ${estimatedLessonCount} lekcji po ~${averageLessonMinutes} minut)\n
      ${course.instructor ? `Instruktor Prowadzący: ${course.instructor}` : ''}
      ${course.textOnly ? 'Format Kursu: Głównie tekstowy' : 'Format Kursu: Mieszany (tekst, potencjalnie inne media)'}
    `;

    const prompt = `\n
      Jesteś **Mistrzem Projektowania Kursów Online** oraz **Ekspertem Merytorycznym** w dziedzinie: **${course.category || 'Ogólnej'}**, ze szczególnym uwzględnieniem tematów: **${course.tags?.join(', ') || 'Szerokich'}**.\n
      \n
      **Twoje Zadanie:** Wygeneruj **KOMPLETNĄ, SZCZEGÓŁOWĄ i OBSZERNĄ** strukturę wraz z **PEŁNĄ TREŚCIĄ** wszystkich lekcji dla kursu online opisanego poniżej. Celem jest stworzenie wartościowego kursu odpowiadającego szacowanemu czasowi trwania.\n
      \n
      ${additionalInfo} \n
      \n
      **Instrukcje Generowania (Krytycznie Ważne!):**\n
      \n
      1.  **Rola Eksperta:** Wciel się w rolę doświadczonego specjalisty. Treść musi być merytoryczna, głęboka, praktyczna i napisana językiem eksperta, ale przystępnym dla grupy docelowej (${course.level || 'nieokreślony'}).\n
      2.  **Struktura Kursu:** Stwórz DOKŁADNIE **${moduleCount} modułów**. W każdym module umieść ŚREDNIO **${lessonsPerModule} lekcji** (dążąc do łącznej liczby lekcji bliskiej **${estimatedLessonCount}**). Nadaj **unikalne ID** (np. "module-1", "lesson-1-1") każdemu modułowi i lekcji.\n
      \n
      3.  **Treść Lekcji (\`content\`):** \n
          *   **Format:** WYŁĄCZNIE **Markdown**. \n
          *   **Długość:** Musi być **OBSZERNA i MERYTORYCZNA** (minimum 500-1000 słów na lekcję, celuj bliżej górnej granicy). Używaj nagłówków (##, ###), list (numerowanych i punktowanych), akapitów, **pogrubień**, *kursywy*, bloków kodu (\`\`\`język ... \`\`\`), cytatów (>), linków (jeśli to sensowne). NIE używaj tagów HTML.\n
          *   **Spójność:** Zgodna z opisem kursu, celami, grupą docelową, poziomem.\n
          *   **Praktyczność:** Dodawaj liczne przykłady, case studies, zadania do wykonania (jeśli pasuje do tematu).\n
          *   **Poprawne Escapowanie:** Cały tekst Markdown dla lekcji musi być poprawnie z-escape'owany, aby stanowić **VALID JSON STRING** wewnątrz pola "content". Oznacza to, że znaki specjalne jak backslash (\\), cudzysłów (") muszą być poprzedzone backslashem (np. \\\\, \\\"). Nowe linie powinny być reprezentowane jako \\n.\n

      4.  **Format Odpowiedzi: STRICT JSON:** \n
          *   Cała odpowiedź musi być **JEDNYM, POPRAWNYM obiektem JSON**. Bez żadnych dodatkowych znaków, komentarzy, notatek czy markdown PRZED lub PO obiekcie JSON (musi zaczynać się od \`{\` i kończyć na \`}\`).\n
          *   **BEZWZGLĘDNIE przestrzegaj schematu JSON poniżej.** Żadnych dodatkowych/brakujących pól.\n
          *   **NIE UŻYWAJ wiszących przecinków (trailing commas)!** Ostatni element w obiekcie lub tablicy NIE MOŻE mieć przecinka na końcu.\n
          *   Wszystkie wartości typu string muszą być w podwójnych cudzysłowach (").\n
          *   **DOKŁADNOŚĆ JSON:** Przed zwróceniem odpowiedzi, **zweryfikuj DWA RAZY**, czy wygenerowany JSON jest w 100% poprawny składniowo i zgodny ze schematem.\n
      {\n
        \"title\": \"string\", // Pełny tytuł kursu \n
        \"description\": \"string\", // Pełny opis kursu \n
        \"modules\": [\n
          {\n
            \"id\": \"string\", // np. \"module-1\"\n
            \"title\": \"string\", \n
            \"description\": \"string\", // Krótki opis modułu\n
            \"lessons\": [\n
              {\n
                \"id\": \"string\", // np. \"lesson-1-1\"\n
                \"title\": \"string\", \n
                \"content\": \"string\" // <<< TUTAJ **OBSZERNA (min. 500-1000+ słów)** treść lekcji w **Markdown** (poprawnie z-escape'owany JSON string).\n
              }\n
              // ...więcej lekcji (średnio ${lessonsPerModule} na moduł, łącznie ~${estimatedLessonCount})\n
            ]\n
          }\n
          // ...więcej modułów (dokładnie ${moduleCount})\n
        ]\n
      }\n
    `;

    try {
      monitoringService.trackEvent('GEMINI_GENERATE_FULL_COURSE_START', { courseId: course.id });
      // Wywołujemy `callGeminiApi` z odpowiednią konfiguracją
      const generationConfig = {
          // Możemy nadpisać domyślne ustawienia `callGeminiApi` jeśli potrzeba
          // np. temperature: 0.7
          // Zwiększenie maxOutputTokens, jeśli domyślne 32k jest za małe dla długich kursów, ale Google ma swoje limity
          // maxOutputTokens: 64000 // Przykład - sprawdź limity modelu
      };

      // Używamy callGeminiApi (które teraz używa proxy)
      const generatedData = await this.callGeminiApi(prompt, this.courseModelName, 'application/json', generationConfig);
      monitoringService.trackEvent('GEMINI_GENERATE_FULL_COURSE_SUCCESS', { courseId: course.id });

      // Walidacja odpowiedzi
      if (generatedData && typeof generatedData.title === 'string' && typeof generatedData.description === 'string' && Array.isArray(generatedData.modules)) {
        // Dodatkowa, bardziej szczegółowa walidacja struktury
        const isValidStructure = generatedData.modules.every((mod: any) =>
            mod && typeof mod.id === 'string' && typeof mod.title === 'string' && typeof mod.description === 'string' && Array.isArray(mod.lessons) &&
            mod.lessons.every((les: any) => les && typeof les.id === 'string' && typeof les.title === 'string' && typeof les.content === 'string')
        );

        if (isValidStructure) {
          // Sprawdzenie czy liczba modułów i lekcji jest zbliżona do oczekiwanej (opcjonalne, bardziej jako ostrzeżenie)
          if (generatedData.modules.length !== moduleCount) {
            console.warn(`Oczekiwano ${moduleCount} modułów, otrzymano ${generatedData.modules.length}`);
            loggingService.warn('Generated module count mismatch', { expected: moduleCount, actual: generatedData.modules.length, courseId: course.id });
          }
          const totalLessons = generatedData.modules.reduce((acc: number, mod: any) => acc + mod.lessons.length, 0);
          if (Math.abs(totalLessons - estimatedLessonCount) > estimatedLessonCount * 0.3) { // Jeśli różnica większa niż 30%
             console.warn(`Oczekiwano ok. ${estimatedLessonCount} lekcji, otrzymano ${totalLessons}`);
             loggingService.warn('Generated lesson count significantly different', { expected: estimatedLessonCount, actual: totalLessons, courseId: course.id });
          }

          return generatedData;
        } else {
            console.error("Wygenerowane dane mają nieprawidłową strukturę wewnętrzną (moduły/lekcje)", generatedData);
            loggingService.error('Gemini generated invalid internal CourseContent structure', { courseId: course.id, generatedDataPreview: JSON.stringify(generatedData).substring(0, 1000) }); // Loguj fragment
            return null;
        }
      } else {
        console.error("Wygenerowane dane nie pasują do oczekiwanej struktury CourseContent (brak kluczowych pól lub niepoprawny typ)", generatedData);
        loggingService.error('Gemini generated invalid root CourseContent structure', { courseId: course.id, generatedDataPreview: JSON.stringify(generatedData).substring(0, 1000) }); // Loguj fragment
        return null;
      }
    } catch (error: any) {
      // Błąd jest już logowany w callGeminiApi
      monitoringService.trackError(new Error('gemini_generate_full_course_error'), { courseId: course.id, error: error.message });
      console.error('Błąd podczas generowania pełnej treści kursu przez Gemini:', error);
      // Rzućmy błąd dalej, aby aiCourseGeneratorService mógł go złapać i użyć fallbacku
      throw error;
    }
  }

  /**
   * Generuje analizę kariery na podstawie odpowiedzi z quizu za pomocą modelu Flash.
   * @param quizAnswers Obiekt zawierający odpowiedzi z quizu.
   * @returns Obiekt analizy kariery lub null w przypadku błędu.
   */
  async generateCareerAnalysis(quizAnswers: { preferences: string[], additional_interests: string, additional_info: string }): Promise<CareerAnalysisData | null> {
    const prompt = `
      Jesteś **Wizjonerem Doradztwa Zawodowego Przyszłości**... (reszta promptu jak wcześniej)
    `;

    try {
      monitoringService.trackEvent('GEMINI_PROXY_ANALYSIS_START');
      const generatedDataString = await this.callProxy(prompt, this.analysisModelName);
      monitoringService.trackEvent('GEMINI_PROXY_ANALYSIS_SUCCESS');

      let generatedData;
      try {
          generatedData = JSON.parse(generatedDataString);
      } catch(e) {
          console.error("Failed to parse career analysis JSON", e);
          return null;
      }

      if (generatedData && Array.isArray(generatedData.sciezkiKariery)) {
        return generatedData as CareerAnalysisData;
      } else {
        loggingService.error('Nieprawidłowa struktura danych z proxy Gemini', { data: generatedData });
        return null;
      }
    } catch (error: any) {
      monitoringService.trackError(new Error('Błąd generowania analizy kariery przez proxy'), { error: error.message });
      throw error;
    }
  }

  /**
   * Generuje prostą treść tekstową na podstawie promptu.
   * Używa modelu Analysis (szybszy).
   */
  async generateContent(prompt: string): Promise<string> {
    try {
      const responseText = await this.callProxy(prompt, this.simpleModelName);
      return responseText || '';
    } catch (error) {
      console.error("Błąd generateContent:", error);
      return '';
    }
  }

  async getCompletion(prompt: string): Promise<any> {
    try {
      const result = await this.callProxy(prompt, this.simpleModelName);
      return result;
    } catch (error) {
      throw error;
    }
  }
}

export const geminiService = new GeminiService();
